import { csvCell } from '../lib/csv';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '../hooks/use-toast';
import { formsApi } from '../lib/api-client';

interface Form {
  id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  _count: {
    submissions: number;
  };
}

interface Submission {
  id: string;
  status: string;
  data: Record<string, any>;
  submittedAt: string;
  reviewNotes?: string;
  member: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export function FormsPage() {
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [reviewStatus, setReviewStatus] = useState('submitted');
  const [reviewNotes, setReviewNotes] = useState('');
  const queryClient = useQueryClient();
  const reviewMutation = useMutation({
    mutationFn: () =>
      formsApi.updateSubmissionStatus(selectedSubmission!.id, reviewStatus, reviewNotes),
    onSuccess: () => {
      setSelectedSubmission((current) =>
        current ? { ...current, status: reviewStatus, reviewNotes } : null,
      );
      queryClient.invalidateQueries({ queryKey: ['form-submissions'] });
    },
  });
  const [submissionsPage, setSubmissionsPage] = useState(1);

  const {
    data: forms,
    isLoading: formsLoading,
    error: formsError,
  } = useQuery({
    queryKey: ['forms'],
    queryFn: () => formsApi.getAllForms(),
  });

  const { data: submissions, isLoading: submissionsLoading } = useQuery({
    queryKey: ['form-submissions', selectedForm?.id, submissionsPage],
    queryFn: () => formsApi.getFormSubmissions(selectedForm!.id, submissionsPage, 20),
    enabled: !!selectedForm,
  });

  const handleExportJSON = () => {
    if (!submissions?.data) return;

    const dataStr = JSON.stringify(submissions.data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedForm?.title.replace(/\s+/g, '-')}-submissions-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    if (!submissions?.data || submissions.data.length === 0) return;

    // Get all unique field keys from submissions
    const fieldKeys = new Set<string>();
    submissions.data.forEach((sub: Submission) => {
      Object.keys(sub.data).forEach((key) => fieldKeys.add(key));
    });

    // Create CSV header
    const headers = [
      'Submission ID',
      'Status',
      'Member Name',
      'Member Email',
      'Submitted At',
      ...Array.from(fieldKeys),
    ];

    // Create CSV rows
    const rows = submissions.data.map((sub: Submission) => {
      const row = [
        sub.id,
        sub.status,
        `${sub.member.firstName} ${sub.member.lastName}`,
        sub.member.email,
        new Date(sub.submittedAt).toLocaleString(),
      ];

      // Add field values
      fieldKeys.forEach((key) => {
        const value = sub.data[key];
        row.push(typeof value === 'object' ? JSON.stringify(value) : String(value || ''));
      });

      return row;
    });

    // Combine into CSV
    const csv = [
      headers.map(csvCell).join(','),
      ...rows.map((row: any[]) => row.map(csvCell).join(',')),
    ].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedForm?.title.replace(/\s+/g, '-')}-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (formsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-slate/20 border-t-slate rounded-full animate-spin" />
      </div>
    );
  }

  if (formsError) {
    return (
      <div className="max-w-4xl">
        <div className="bg-white rounded-card shadow-card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-clay/10 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-clay"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.65}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-charcoal mb-2">Error loading forms</h3>
          <p className="text-muted text-sm">
            {(formsError as any)?.message || 'Unable to load forms. Please try again.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      {!selectedForm ? (
        // Forms List View
        <>
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-semibold text-charcoal mb-2">
              Forms Management
            </h1>
            <p className="text-muted text-base">View form submissions and export data</p>
          </div>

          <div className="bg-slate/10 border border-slate/20 rounded-card p-4 mb-6">
            <p className="text-sm text-charcoal">
              <span className="font-medium">Note:</span> Form builder is not available in Phase 1.
              New forms are added via seed scripts.
            </p>
          </div>

          {!forms || forms.length === 0 ? (
            <div className="bg-white rounded-card shadow-card p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-workspace flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-muted"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">No forms found</h3>
              <p className="text-muted text-sm">Forms will appear here once created</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {forms?.map((form: Form) => (
                <div
                  key={form.id}
                  className="bg-white rounded-card shadow-card p-6 hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => setSelectedForm(form)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-charcoal group-hover:text-slate transition-colors">
                      {form.title}
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        form.status === 'published'
                          ? 'bg-slate/10 text-slate border-slate/20'
                          : 'bg-workspace text-muted border-line'
                      }`}
                    >
                      {form.status}
                    </span>
                  </div>

                  <p className="text-sm text-muted mb-4 leading-relaxed">{form.description}</p>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted">
                      {form._count?.submissions || 0} submission
                      {(form._count?.submissions || 0) !== 1 ? 's' : ''}
                    </span>
                    <span className="text-slate font-medium group-hover:underline">
                      View Submissions →
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        // Form Submissions View
        <div>
          <div className="mb-6">
            <button
              onClick={() => {
                setSelectedForm(null);
                setSubmissionsPage(1);
              }}
              className="inline-flex items-center gap-2 text-slate hover:text-charcoal mb-4 text-sm font-medium transition-colors"
            >
              <span>←</span> Back to Forms
            </button>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-semibold text-charcoal mb-2">
                  {selectedForm.title}
                </h2>
                <p className="text-muted text-base">{selectedForm.description}</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleExportJSON}
                  disabled={!submissions?.data || submissions.data.length === 0}
                  className="px-4 py-2.5 border border-line rounded-button hover:bg-workspace transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium text-charcoal"
                >
                  Export this page (JSON)
                </button>
                <button
                  onClick={handleExportCSV}
                  disabled={!submissions?.data || submissions.data.length === 0}
                  className="px-4 py-2.5 bg-charcoal text-white rounded-button text-sm font-semibold hover:bg-charcoal/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-button"
                >
                  Export this page (CSV)
                </button>
              </div>
            </div>
          </div>

          {submissionsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-slate/20 border-t-slate rounded-full animate-spin" />
            </div>
          ) : !submissions?.data || submissions.data.length === 0 ? (
            <div className="bg-white rounded-card shadow-card p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-workspace flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-muted"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">No submissions yet</h3>
              <p className="text-muted text-sm">
                Submissions will appear here once members fill out this form
              </p>
            </div>
          ) : (
            <>
              {/* Submissions Table */}
              <div className="bg-white rounded-card shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-workspace border-b border-line">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                          Member
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                          Submitted
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {submissions?.data?.map((submission: Submission) => (
                        <tr key={submission.id} className="hover:bg-workspace transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="font-medium text-charcoal">
                              {submission.member.firstName} {submission.member.lastName}
                            </div>
                            <div className="text-sm text-muted">{submission.member.email}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium border capitalize ${
                                submission.status === 'approved'
                                  ? 'bg-slate/10 text-slate border-slate/20'
                                  : submission.status === 'rejected'
                                    ? 'bg-clay/10 text-clay border-clay/20'
                                    : submission.status === 'under_review'
                                      ? 'bg-orange/10 text-orange border-orange/20'
                                      : 'bg-workspace text-muted border-line'
                              }`}
                            >
                              {submission.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-muted">
                            {new Date(submission.submittedAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => {
                                setSelectedSubmission(submission);
                                setReviewStatus(submission.status);
                                setReviewNotes(submission.reviewNotes || '');
                                reviewMutation.reset();
                              }}
                              className="text-slate hover:underline font-medium"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {submissions?.pagination && submissions.pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-6">
                  <button
                    onClick={() => setSubmissionsPage((p) => Math.max(1, p - 1))}
                    disabled={submissionsPage === 1}
                    className="px-4 py-2 border border-line rounded-button hover:bg-workspace transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium text-charcoal"
                  >
                    ← Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-muted">
                    Page <span className="font-medium text-charcoal">{submissionsPage}</span> of{' '}
                    <span className="font-medium text-charcoal">
                      {submissions.pagination.totalPages}
                    </span>
                  </span>
                  <button
                    onClick={() => setSubmissionsPage((p) => p + 1)}
                    disabled={submissionsPage >= submissions.pagination.totalPages}
                    className="px-4 py-2 border border-line rounded-button hover:bg-workspace transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium text-charcoal"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Submission Details Modal */}
      {selectedSubmission && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedSubmission(null)}
        >
          <div
            className="bg-white rounded-card shadow-card p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-charcoal mb-1">Submission Details</h2>
                <p className="text-muted">
                  {selectedSubmission.member.firstName} {selectedSubmission.member.lastName}
                </p>
              </div>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-muted hover:text-charcoal text-2xl font-light leading-none"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 pb-6 border-b border-line">
                <InfoField label="Email" value={selectedSubmission.member.email} />
                <InfoField
                  label="Status"
                  value={selectedSubmission.status.replace('_', ' ')}
                  capitalize
                />
                <InfoField
                  label="Submitted"
                  value={new Date(selectedSubmission.submittedAt).toLocaleString()}
                />
                <InfoField label="Submission ID" value={selectedSubmission.id} mono />
              </div>

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  reviewMutation.mutate();
                }}
                className="space-y-3"
              >
                <label className="block">
                  Review status
                  <select
                    aria-label="Review status"
                    value={reviewStatus}
                    onChange={(event) => setReviewStatus(event.target.value)}
                    className="block border rounded p-2 w-full"
                  >
                    {['submitted', 'under_review', 'approved', 'rejected'].map((status) => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  Review notes
                  <textarea
                    aria-label="Review notes"
                    maxLength={1000}
                    value={reviewNotes}
                    onChange={(event) => setReviewNotes(event.target.value)}
                    className="block border rounded p-2 w-full"
                  />
                </label>
                {reviewMutation.isError && (
                  <p role="alert">{getErrorMessage(reviewMutation.error)}</p>
                )}
                {reviewMutation.isSuccess && <p role="status">Review saved</p>}
                <button
                  disabled={reviewMutation.isPending}
                  className="bg-charcoal text-white rounded px-4 py-2"
                >
                  {reviewMutation.isPending ? 'Saving...' : 'Save review'}
                </button>
              </form>
              <div>
                <h3 className="text-lg font-semibold text-charcoal mb-4">Form Data</h3>
                <div className="space-y-4">
                  {Object.entries(selectedSubmission.data).map(([key, value]) => (
                    <div key={key} className="pb-4 border-b border-line last:border-0">
                      <label className="block text-sm font-medium text-muted capitalize mb-2">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <div className="text-charcoal">
                        {typeof value === 'object' ? (
                          <pre className="bg-workspace p-4 rounded-button text-sm overflow-auto font-mono">
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        ) : (
                          <p className="text-base">{String(value)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-line">
              <button
                onClick={() => setSelectedSubmission(null)}
                className="w-full px-4 py-3 bg-charcoal text-white rounded-button text-sm font-semibold hover:bg-charcoal/90 active:scale-[0.98] transition-all shadow-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({
  label,
  value,
  capitalize,
  mono,
}: {
  label: string;
  value: string;
  capitalize?: boolean;
  mono?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-muted mb-1">{label}</label>
      <p
        className={`text-charcoal ${capitalize ? 'capitalize' : ''} ${mono ? 'text-sm font-mono' : ''}`}
      >
        {value}
      </p>
    </div>
  );
}

export default FormsPage;
